import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { 
  Star, 
  Smile, 
  Loader2, 
  CheckCircle, 
  Clock, 
  XCircle, 
  CalendarIcon, 
  Filter, 
  Video,
  ExternalLink,
  User,
  Shield
} from "lucide-react";
import type { TestimonialWithUser } from "@shared/schema";

export default function AdminTestimonialsPage() {
  const { toast } = useToast();
  const { isMasterAdmin } = useAuth();
  const [, setLocation] = useLocation();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [selectedTestimonial, setSelectedTestimonial] = useState<TestimonialWithUser | null>(null);
  const [adminNote, setAdminNote] = useState("");
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approved' | 'rejected' | null>(null);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (ratingFilter !== "all") params.set("rating", ratingFilter);
    if (startDate) params.set("startDate", startDate.toISOString());
    if (endDate) params.set("endDate", endDate.toISOString());
    return params.toString();
  };

  const { data: testimonials = [], isLoading } = useQuery<TestimonialWithUser[]>({
    queryKey: ['/api/admin/testimonials', statusFilter, ratingFilter, startDate, endDate],
    queryFn: async () => {
      const query = buildQueryParams();
      const response = await fetch(`/api/admin/testimonials${query ? `?${query}` : ''}`);
      if (!response.ok) throw new Error("Failed to fetch testimonials");
      return response.json();
    },
    enabled: isMasterAdmin,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, note }: { id: string; status: string; note: string | null }) => {
      const response = await apiRequest('PATCH', `/api/admin/testimonials/${id}/status`, {
        status,
        adminNote: note,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/testimonials'] });
      toast({
        title: "Updated",
        description: "Testimonial status has been updated.",
      });
      setActionDialogOpen(false);
      setSelectedTestimonial(null);
      setAdminNote("");
      setPendingAction(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update testimonial status",
        variant: "destructive",
      });
    },
  });

  const handleAction = (testimonial: TestimonialWithUser, action: 'approved' | 'rejected') => {
    setSelectedTestimonial(testimonial);
    setPendingAction(action);
    setAdminNote("");
    setActionDialogOpen(true);
  };

  const confirmAction = () => {
    if (selectedTestimonial && pendingAction) {
      updateStatusMutation.mutate({
        id: selectedTestimonial.id,
        status: pendingAction,
        note: adminNote.trim() || null,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setRatingFilter("all");
    setStartDate(undefined);
    setEndDate(undefined);
  };

  if (!isMasterAdmin) {
    return (
      <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6">
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">Only master administrators can access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6">
      <div className="flex items-center gap-3 mb-8">
        <Smile className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Testimonial Management</h1>
          <p className="text-muted-foreground">Review and moderate user testimonials</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="select-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Rating</Label>
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger data-testid="select-rating-filter">
                  <SelectValue placeholder="All ratings" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testimonials Table */}
      <Card>
        <CardHeader>
          <CardTitle>Testimonials ({testimonials.length})</CardTitle>
          <CardDescription>
            Click to view details and approve or reject testimonials
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Smile className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No testimonials found matching your filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Testimonial</TableHead>
                  <TableHead>Video</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {testimonials.map((testimonial) => (
                  <TableRow 
                    key={testimonial.id} 
                    data-testid={`row-testimonial-${testimonial.id}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setLocation(`/admin/testimonials/${testimonial.id}/edit`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={testimonial.photoUrl || testimonial.user?.profileImageUrl || undefined} />
                          <AvatarFallback>
                            <User className="w-4 h-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">
                            {testimonial.user?.firstName} {testimonial.user?.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground">{testimonial.user?.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= testimonial.rating
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="truncate">{testimonial.testimonialText}</p>
                    </TableCell>
                    <TableCell>
                      {testimonial.videoUrl && /^https?:\/\//i.test(testimonial.videoUrl) ? (
                        <a
                          href={testimonial.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          <Video className="w-4 h-4" />
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(testimonial.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(testimonial.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        {testimonial.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAction(testimonial, 'approved');
                              }}
                              data-testid={`button-approve-${testimonial.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAction(testimonial, 'rejected');
                              }}
                              data-testid={`button-reject-${testimonial.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {testimonial.status !== 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTestimonial(testimonial);
                              setAdminNote(testimonial.adminNote || "");
                              setActionDialogOpen(true);
                              setPendingAction(null);
                            }}
                            data-testid={`button-view-${testimonial.id}`}
                          >
                            View
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {pendingAction === 'approved' ? 'Approve Testimonial' : 
               pendingAction === 'rejected' ? 'Reject Testimonial' : 
               'Testimonial Details'}
            </DialogTitle>
            <DialogDescription>
              {pendingAction ? 'Add an optional note for the user' : 'View testimonial details'}
            </DialogDescription>
          </DialogHeader>

          {selectedTestimonial && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={selectedTestimonial.photoUrl || selectedTestimonial.user?.profileImageUrl || undefined} />
                  <AvatarFallback>
                    <User className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">
                    {selectedTestimonial.user?.firstName} {selectedTestimonial.user?.lastName}
                  </p>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= selectedTestimonial.rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <p className="italic">"{selectedTestimonial.testimonialText}"</p>
              </div>

              {selectedTestimonial.videoUrl && /^https?:\/\//i.test(selectedTestimonial.videoUrl) && (
                <a
                  href={selectedTestimonial.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <Video className="w-4 h-4" />
                  Watch Video Testimonial
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}

              {pendingAction && (
                <div className="space-y-2">
                  <Label>Admin Note (Optional)</Label>
                  <Textarea
                    placeholder="Add a note for the user explaining your decision..."
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    rows={3}
                    data-testid="input-admin-note"
                  />
                </div>
              )}

              {!pendingAction && selectedTestimonial.adminNote && (
                <div className="space-y-2">
                  <Label>Admin Note</Label>
                  <p className="text-sm bg-muted p-3 rounded">{selectedTestimonial.adminNote}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            {pendingAction && (
              <Button
                onClick={confirmAction}
                disabled={updateStatusMutation.isPending}
                className={pendingAction === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                variant={pendingAction === 'rejected' ? 'destructive' : 'default'}
              >
                {updateStatusMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : pendingAction === 'approved' ? (
                  <CheckCircle className="w-4 h-4 mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                {pendingAction === 'approved' ? 'Approve' : 'Reject'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
